import { z } from "zod";

/**
 * Semantic algorithm operations (Master Spec §15, §28).
 * These are the ONLY vocabulary allowed to represent what an algorithm
 * did during a level. They drive gameplay feedback, trace generation,
 * analytics and replay. Raw Three.js/DOM events must never be recorded
 * as educational algorithm events — only these.
 *
 * Adding an operation here is an architecture-level decision because it
 * is shared across the game, the trace UI, and learning analytics.
 */
export const SearchOperationType = z.enum([
  "SCAN",
  "COMPARE",
  "FOUND",
  "NOT_FOUND",
  "CHOOSE_LEFT",
  "CHOOSE_RIGHT",
  "DISCARD_RANGE",
]);
export type SearchOperationType = z.infer<typeof SearchOperationType>;

export const SortOperationType = z.enum([
  "COMPARE",
  "SWAP",
  "KEEP",
  "SELECT_MIN",
  "SELECT_MAX",
  "PLACE",
  "SHIFT",
  "INSERT",
  "PASS_COMPLETE",
]);
export type SortOperationType = z.infer<typeof SortOperationType>;

export const OperationType = z.union([SearchOperationType, SortOperationType]);
export type OperationType = z.infer<typeof OperationType>;

/**
 * A single semantic event emitted by the Algorithm Engine (never by the
 * renderer). Operation sequences form the algorithm trace (Master Spec §28)
 * and the analytics stream (§40). `payload` is intentionally loose here;
 * concrete per-operation payload shapes are refined per algorithm module
 * in `game/src/algorithms/*` starting Phase 02.
 */
export const AlgorithmOperation = z.object({
  sequence: z.number().int().nonnegative(),
  type: OperationType,
  timestampMs: z.number().nonnegative(),
  indices: z.array(z.number().int().nonnegative()).optional(),
  values: z.array(z.number()).optional(),
  range: z.tuple([z.number().int(), z.number().int()]).optional(),
  note: z.string().optional(),
});
export type AlgorithmOperation = z.infer<typeof AlgorithmOperation>;
